import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import Select from "react-select";
import { types as OPPORTUNITY_TYPES } from "../lib/opportunityTypes";

const PROGRAM_TYPES = [
  "Biology",
  "Business Administration", 
  "Chemical Engineering",
  "Chemistry",
  "Computer Science",
  "Electrical Engineering",
  "Mechanical Engineering",
  "Physics",
  "Psychology"
];

const EditOpportunityModal = ({ isOpen, onClose, onSubmit, opportunity }) => {
  const [programType, setProgramType] = useState("");
  const [hostName, setHostName] = useState("");
  const [hostPhoneNumber, setHostPhoneNumber] = useState("");
  const [contactPerson, setContactPerson] = useState(""); // Added contact person state
  const [hostSite, setHostSite] = useState("");
  const [opportunityType, setOpportunityType] = useState([]);
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [description, setDescription] = useState("");
  const [requiredDress, setRequiredDress] = useState("");
  const [schedule, setSchedule] = useState({});
  const [siteVisit, setSiteVisit] = useState("No"); // Possible values: 'Yes', 'No', 'N/A'
  const [dateOfVisit, setDateOfVisit] = useState("");
  const [visitBy, setVisitBy] = useState("");

  const [isActive, setIsActive] = useState(true);

  const daysOfWeek = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  // Tab state
  const [activeTab, setActiveTab] = useState("host"); // 'host' or 'details'

  useEffect(() => {
    if (isOpen && opportunity) {
      setActiveTab("host");

      setProgramType(opportunity.programType || "");
      setHostSite(opportunity.hostSite || "");
      setHostName(opportunity.hostName || "");
      setHostPhoneNumber(opportunity.hostPhoneNumber || "");
      setContactPerson(opportunity.contactPerson || ""); // Added contact person update
      setStreetAddress(opportunity.streetAddress || "");
      setCity(opportunity.city || "");
      setStateProvince(opportunity.stateProvince || "");
      setPostalCode(opportunity.postalCode || "");
      setDescription(opportunity.description || "");
      setRequiredDress(opportunity.requiredDress || "");

      const type = OPPORTUNITY_TYPES.find(
        (type) =>
          type.value === opportunity.opportunityType ||
          type.label === opportunity.opportunityType
      );
      setOpportunityType(type ? type.value : opportunity.opportunityType || "");

      setSchedule(opportunity.schedule || {});

      setSiteVisit(opportunity.siteVisit === true ? "Yes" : "No");
      setDateOfVisit(opportunity.dateOfVisit || "");
      setVisitBy(opportunity.visitBy || "");
      setIsActive(opportunity.isActive !== false);
    }
  }, [isOpen, opportunity]);

  const handleDayToggle = (day) => {
    setSchedule((prevSchedule) => {
      if (prevSchedule[day]) {
        const { [day]: _, ...rest } = prevSchedule;
        return rest;
      } else {
        return {
          ...prevSchedule,
          [day]: { startTime: "", endTime: "" },
        };
      }
    });
  };

  const handleTimeChange = (day, field, value) => {
    setSchedule((prevSchedule) => ({
      ...prevSchedule,
      [day]: {
        ...prevSchedule[day],
        [field]: value,
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all steps before submission
    if (!validateStep("host")) {
      setActiveTab("host");
      return;
    }
    if (!validateStep("details")) {
      setActiveTab("details");
      return;
    }
    if (!validateStep("schedule")) {
      setActiveTab("schedule");
      return;
    }
    // Add final validation for schedule
    if (!validateStep("final")) {
      return;
    }

    const updatedOpportunity = {
      id: opportunity.id,
      programType,
      hostName,
      hostPhoneNumber,
      contactPerson, // Added contact person to updatedOpportunity
      hostSite,
      streetAddress,
      city,
      stateProvince,
      postalCode,
      description,
      requiredDress,
      opportunityType,
      isActive,
      schedule,
      siteVisit: siteVisit === "Yes",
      dateOfVisit: siteVisit === "Yes" ? dateOfVisit : null,
      visitBy: siteVisit === "Yes" ? visitBy : null,
      students: opportunity.students || [],
    };

    onSubmit(updatedOpportunity);
    toast.success("Opportunity updated successfully!");

    // Reset form fields
    setHostName("");
    setHostPhoneNumber("");
    setContactPerson(""); // Reset contact person
    setHostSite("");
    setStreetAddress("");
    setCity("");
    setStateProvince("");
    setPostalCode("");
    setDescription("");
    setRequiredDress("");
    setOpportunityType("");
    setIsActive(true);
    setSchedule({});
    setSiteVisit("No");
    setDateOfVisit("");
    setVisitBy("");
    setActiveTab("host");
    onClose();
  };

  // Add these navigation functions
  const handleNext = () => {
    if (activeTab === "host" && validateStep("host")) {
      setActiveTab("details");
    } else if (activeTab === "details" && validateStep("details")) {
      setActiveTab("schedule");
    }
  };

  const handleBack = () => {
    if (activeTab === "schedule") {
      setActiveTab("details");
    } else if (activeTab === "details") {
      setActiveTab("host");
    }
  };

  // Add the validateStep function
  const validateStep = (step) => {
    switch (step) {
      case "host":
        if (!hostName || !hostPhoneNumber) {
          toast.error("Please fill in all required Host Information fields.");
          setActiveTab("host");
          return false;
        }
        if (!streetAddress || !city || !stateProvince || !postalCode) {
          toast.error("Please fill in the complete address information.");
          setActiveTab("host");
          return false;
        }
        return true;
      case "details":
        if (!opportunityType) {
          toast.error("Please select an Opportunity Type.");
          return false;
        }
        return true;
      case "schedule":
        // Only validate site visit fields during navigation
        if (siteVisit === "Yes") {
          if (!dateOfVisit) {
            toast.error("Please select a date of visit.");
            setActiveTab("schedule");
            return false;
          }
          if (!visitBy) {
            toast.error("Please enter the name of the visitor.");
            setActiveTab("schedule");
            return false;
          }
        }
        return true;
      case "final":
        // Check if at least one day is selected - only during final submission
        if (Object.keys(schedule).length === 0) {
          toast.error("Please select at least one day in the schedule.");
          setActiveTab("schedule");
          return false;
        }
        // Validate schedule times if any days are selected
        for (const day in schedule) {
          const { startTime, endTime } = schedule[day];
          if (!startTime || !endTime) {
            toast.error(`Please enter start and end times for ${day}.`);
            setActiveTab("schedule");
            return false;
          }
          if (startTime >= endTime) {
            toast.error(`End time must be after start time for ${day}.`);
            setActiveTab("schedule");
            return false;
          }
        }
        return true;
      default:
        return true;
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-modal-title"
    >
      <div className="bg-white rounded-lg w-full max-w-3xl p-6 relative overflow-y-auto max-h-screen">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          aria-label="Close Modal"
        >
          <X size={20} />
        </button>
        {/* Modal Title */}
        <h2 id="edit-modal-title" className="text-2xl font-bold mb-6">
          Edit Opportunity
        </h2>
        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-4 border-b">
            <button
              type="button"
              onClick={() => setActiveTab("host")}
              className={`py-2 px-4 ${
                activeTab === "host"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              Host Information
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("details")}
              className={`py-2 px-4 ${
                activeTab === "details"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              Opportunity Details
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("schedule")}
              className={`py-2 px-4 ${
                activeTab === "schedule"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              Schedule
            </button>
          </nav>
        </div>
        <form onSubmit={handleSubmit}>
          {/* Host Information Tab */}
          {activeTab === "host" && (
            <div className="space-y-6">
              {/* Host Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Host Site */}
                {/* <div>
                  <label className="block text-gray-700">
                    Host Site<span className="text-red-500">*</span>
                  </label>
                  <input
                    id="edit-host-site"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={hostSite}
                    onChange={(e) => setHostSite(e.target.value)}
                    required
                    placeholder="Enter host site"
                  />
                </div> */}
                {/* Host Name */}
                <div>
                  <label className="block text-gray-700">
                    Host Name<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={hostName}
                    onChange={(e) => setHostName(e.target.value)}
                    required
                    placeholder="Enter host name"
                  />
                </div>
                {/* Host Phone Number */}
                <div>
                  <label className="block text-gray-700">
                    Host Phone Number<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    className="w-full border rounded px-3 py-2"
                    value={hostPhoneNumber}
                    onChange={(e) => setHostPhoneNumber(e.target.value)}
                    required
                    placeholder="Enter host phone number"
                  />
                </div>
                {/* Contact Person */}
                <div>
                  <label className="block text-gray-700">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={contactPerson}
                    onChange={(e) => setContactPerson(e.target.value)}
                    placeholder="Enter contact person"
                  />
                </div>
              </div>
              {/* Address Information */}
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700">
                    Street Address<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={streetAddress}
                    required
                    onChange={(e) => setStreetAddress(e.target.value)}
                    placeholder="Enter street address"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-700">
                      City<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded px-3 py-2"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      required
                      placeholder="Enter city"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700">
                      State/Province<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded px-3 py-2"
                      value={stateProvince}
                      onChange={(e) => setStateProvince(e.target.value)}
                      required
                      placeholder="Enter state or province"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700">
                      Postal Code<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded px-3 py-2"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      required
                      placeholder="Enter postal code"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Opportunity Details Tab */}
          {activeTab === "details" && (
            <div className="space-y-6">
              <div className="space-y-4">
                {/* Program Type Field */}
                <div>
                  <label className="block text-gray-700">
                    Program<span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={programType ? { value: programType, label: programType } : null}
                    onChange={(selected) => setProgramType(selected ? selected.value : "")}
                    options={PROGRAM_TYPES.map(type => ({ value: type, label: type }))}
                    className="mt-1"
                    placeholder="Select program"
                    isSearchable
                    required
                  />
                </div>
                {/* Active/Inactive Checkbox */}
                <div>
                  <label className="block text-gray-700">Status</label>
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="mr-2"
                    />
                    <span>{isActive ? "Active" : "Inactive"}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700">
                    Opportunity Type<span className="text-red-500">*</span>
                  </label>
                  <Select
                    isMulti
                    value={OPPORTUNITY_TYPES.filter(
                      (type) => opportunityType.includes(type.value)
                    )}
                    onChange={(selected) =>
                      setOpportunityType(selected.map(option => option.value))
                    }
                    options={OPPORTUNITY_TYPES}
                    className="mt-1"
                    placeholder="Select opportunity types"
                    isSearchable
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700">Description</label>
                  <textarea
                    className="w-full border rounded px-3 py-2"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter description"
                    rows="4"
                  />
                </div>
                <div>
                  <label className="block text-gray-700">Required Dress</label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={requiredDress}
                    onChange={(e) => setRequiredDress(e.target.value)}
                    placeholder="Enter required dress"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Schedule Tab */}
          {activeTab === "schedule" && (
            <div className="space-y-6">
              {/* Site Visit Section */}
              <div>
                <label className="block text-gray-700">Site Visit</label>
                <div className="flex items-center space-x-4 mt-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="siteVisit"
                      value="Yes"
                      checked={siteVisit === "Yes"}
                      onChange={(e) => setSiteVisit(e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="siteVisit"
                      value="No"
                      checked={siteVisit === "No"}
                      onChange={(e) => setSiteVisit(e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="siteVisit"
                      value="N/A"
                      checked={siteVisit === "N/A"}
                      onChange={(e) => setSiteVisit(e.target.value)}
                      className="mr-2"
                    />
                    N/A
                  </label>
                </div>
                {siteVisit === "Yes" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-gray-700">
                        Date of Visit<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        className="w-full border rounded px-3 py-2"
                        value={dateOfVisit}
                        onChange={(e) => setDateOfVisit(e.target.value)}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700">
                        Visit By<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full border rounded px-3 py-2"
                        value={visitBy}
                        onChange={(e) => setVisitBy(e.target.value)}
                        required
                        placeholder="Enter visitor's name"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Potential Schedule */}
              <div>
                <label className="block text-gray-700">
                  Potential Schedule
                </label>
                <div className="mt-2 space-y-4">
                  {daysOfWeek.map((day) => (
                    <div key={day} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={schedule.hasOwnProperty(day)}
                        onChange={() => handleDayToggle(day)}
                        className="mr-2"
                      />
                      <label className="mr-4">{day}</label>
                      {schedule.hasOwnProperty(day) && (
                        <div className="flex space-x-2">
                          <input
                            type="time"
                            value={schedule[day].startTime}
                            onChange={(e) =>
                              handleTimeChange(day, "startTime", e.target.value)
                            }
                            className="border rounded px-2 py-1"
                            required
                          />
                          <span>to</span>
                          <input
                            type="time"
                            value={schedule[day].endTime}
                            onChange={(e) =>
                              handleTimeChange(day, "endTime", e.target.value)
                            }
                            className="border rounded px-2 py-1"
                            required
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Form Buttons */}
          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={handleBack}
              className={`bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded ${
                activeTab === "host" ? "invisible" : ""
              }`}
            >
              Back
            </button>
            <div className="ml-auto">
              {activeTab !== "schedule" && (
                <button
                  type="button"
                  onClick={handleNext}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded"
                >
                  Next
                </button>
              )}
              {activeTab === "schedule" && (
                <button
                  type="submit"
                  className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded"
                >
                  Submit
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Define PropTypes for better type checking
EditOpportunityModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  opportunity: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    hostName: PropTypes.string,
    hostPhoneNumber: PropTypes.string,
    contactPerson: PropTypes.string, // Added contactPerson propType
    hostSite: PropTypes.string,
    streetAddress: PropTypes.string,
    city: PropTypes.string,
    stateProvince: PropTypes.string,
    postalCode: PropTypes.string,
    description: PropTypes.string,
    requiredDress: PropTypes.string,
    totalHours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    schedule: PropTypes.object,
    siteVisit: PropTypes.bool,
    dateOfVisit: PropTypes.string,
    visitBy: PropTypes.string,
    isActive: PropTypes.bool,
  }).isRequired,
};

export default EditOpportunityModal;